import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';

const NotificationContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch stored notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [notifsRes, countRes] = await Promise.all([
        api.get('/api/notifications?limit=20'),
        api.get('/api/notifications/unread-count')
      ]);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.count);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [user]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notifId) => {
    try {
      await api.post(`/api/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, []);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    try {
      await api.post('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  }, []);

  // Get auth token for WebSocket
  const getToken = useCallback(() => {
    return localStorage.getItem('access_token');
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!user) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setConnected(false);
      return;
    }

    let isCancelled = false;

    const connect = () => {
      const token = getToken();
      if (!token || isCancelled) return;

      const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
      const wsUrl = `${wsProtocol}://${API_URL.replace(/^https?:\/\//, '')}/api/ws/${token}`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!isCancelled) setConnected(true);
        };

        ws.onmessage = (event) => {
          if (isCancelled) return;
          try {
            const notif = JSON.parse(event.data);
            if (notif.type === 'connection') return; // Skip connection confirmation

            // Show toast notification
            const toastType = notif.type === 'error' ? 'error' : 
                              notif.type === 'warning' ? 'warning' : 'success';
            toast[toastType](notif.title, { description: notif.message });

            // Add to local state
            setNotifications(prev => [{
              id: crypto.randomUUID(),
              type: notif.type,
              title: notif.title,
              message: notif.message,
              data: notif.data,
              read: false,
              created_at: notif.timestamp
            }, ...prev].slice(0, 50));
            setUnreadCount(prev => prev + 1);
          } catch {
            // ignore parse errors
          }
        };

        ws.onclose = () => {
          if (!isCancelled) {
            setConnected(false);
            reconnectTimeoutRef.current = setTimeout(connect, 5000);
          }
        };

        ws.onerror = () => {
          ws.close();
        };

        // Keepalive ping every 30s
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);

        ws._pingInterval = pingInterval;
      } catch {
        if (!isCancelled) {
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        }
      }
    };

    fetchNotifications();
    connect();

    return () => {
      isCancelled = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user, getToken, fetchNotifications]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      connected,
      markAsRead,
      markAllRead,
      fetchNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
